<template>
  <div class="ml-3 mb-3">
  <h5 v-if="label">{{label}}</h5>
  <div ref="table"></div>
  <div class="error-div mt-2" v-if="!disableValidationMessage && validationMessage">
      <div v-html="validationMessage"></div>
  </div>
</div>
</template>

<script>

import { TabulatorFull as Tabulator } from 'tabulator-tables'; 
import changeTracker from "../utils/change-tracker.js";

export default {
    props: {
      value: { type: Array, default: () => [{}] },
      columns: { type: Array, required: true },
      label: { type: String, default: null },
      isReadOnly: { type: Boolean, default: false },
      showRowNumbers: { type: Boolean, default: false },
      disableChangeTracker: { type: Boolean, default: false },
      disableValidationMessage: { type: Boolean, default: false },
      disableValidation: { type: Boolean, default: false },     // red borders around invalid cells
      disableRangeSelection: { type: Boolean, default: false }, // highlight column by column header
      disableKeyBindings: { type: Boolean, default: false },    // Ctrl + C, Ctrl + V, Ctrl + Z, Ctrl + Y, etc
      disableAddRow: { type: Boolean, default: false }, 
      disableDeleteRow: { type: Boolean, default: false },
      isValidWhenEmpty: { type: Boolean, default: false },
      movableRows: { type: Boolean, default: false },
    },
  data() {
    // https://tabulator.info/docs/5.6
    return {
      tabulator: null,
      tableData: this.value,
      tableColumns: [],
      validationMessage: null
    };
  },
  created() {
  },
  mounted() {
    this.refreshData();
  },
  computed: {
    tableSettings() {
      let settings = {
        layout: "fitData",
        height: 'auto',
        autoscroll: false,

        columnHeaderVertAlign:"bottom",
        columnDefaults: {
          editor: !this.isReadOnly,
          width: 100,
          headerHozAlign: "center",
          resizable: "header",
          headerSort: false
        },
      }

      if (!this.isReadOnly) {
        settings = {
          ...settings, 
          editTriggerEvent: "dblclick",
          validationMode: "highlight",
          history: !this.disableKeyBindings,
          movableRows: this.movableRows && !this.isReadOnly,
          clipboard: true,
          clipboardCopyStyled: false,
          clipboardCopyConfig: { rowHeaders: false, columnHeaders: false },
          clipboardCopyRowRange: "range",
          clipboardPasteParser: "range",
          clipboardPasteAction: "range",
        }

        settings.columnDefaults.editor = "input";
        
        if (!this.disableRangeSelection) {
          settings = {
            ...settings,
            selectableRange: 1,
            selectableRangeColumns: true,
            selectableRangeClearCells: true,
            selectableRangeClearCellsValue: null
          }
        }
      } 

      return settings;
    },
  },
  methods: {
    refreshData() {
      this.validationMessage = null;
      if (!this.tableData || this.tableData.length === 0) this.tableData = [{}];

      if (this.tabulator) this.tabulator.destroy();
      if (!this.$refs.table) return;

      this.setTableColumns();

      this.tabulator = new Tabulator(this.$refs.table, {
        data: this.tableData,
        columns: this.tableColumns,
        ...this.tableSettings
      });

      this.tabulator.on("tableBuilt", () => {
        if (this.$refs.table)
          this.$refs.table.querySelector(".tabulator-tableholder").style.overflow = 'hidden';
      });

      if (!this.isReadOnly && !this.disableValidation) {
        this.tabulator.on("tableBuilt", () => {
          let allEmpty = this.tabulator.getRows().every(row => 
            row.getCells().every(cell => cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === "")
          );
          if (!allEmpty) this.validate();
        });

        this.tabulator.on("cellEdited", (cell) => { 
          cell.validate(); 
          this.updateValidationMessages(); 
        });
        
        if (!this.disableKeyBindings) {
          this.tabulator.on("historyUndo", () => this.validate());
          this.tabulator.on("historyRedo", () => this.validate());
          this.tabulator.on("clipboardPasted", () => this.validate());
        }
      }
    },
    getData() {
      const internalFields = ['addRow', 'deleteRow'];
      return this.tabulator.getData().map(d =>
        Object.fromEntries(Object.entries(d).filter(([key]) => !internalFields.includes(key)))
      );
    },
    setTableColumns() {
      this.tableColumns = this.columns;

      if (this.showRowNumbers) 
        this.tableColumns = [{ resizable: false, frozen: true, width: 40, hozAlign: "center", formatter: "rownum", headerSort: false }, ...this.tableColumns]

      if (this.movableRows && !this.isReadOnly)
        this.tableColumns = [{ resizable: false, frozen: true, width: 30, rowHandle: true, formatter: "handle", headerSort: false, minWidth: 30 }, ...this.tableColumns];

      if (this.isReadOnly) {
        this.tableColumns.forEach(c => {
          c.editor = false;
          c.validator = null;
        })
      }

      let buttonFields = { width: 40, hozAlign: "center", headerSort: false, editor: false };
      if (!this.disableAddRow && !this.isReadOnly && !this.tableColumns.some(c => c.field == "addRow")) {
        this.tableColumns.push({ ...buttonFields, field: "addRow", 
          formatter: () => '<i class="fa fa-plus cursor-pointer" style="color: green; font-size: 16px;" title:"Insert Below" />',
          cellClick: function(e, cell) { if (cell) cell.getTable().addRow({}, false, cell.getRow()); }
        });
      }
      if (!this.disableDeleteRow && !this.isReadOnly && !this.tableColumns.some(c => c.field === "deleteRow")) {
        this.tableColumns.push({ ...buttonFields, field: "deleteRow",
          formatter: () => '<i class="fa fa-times cursor-pointer" style="color: red; font-size: 16px;" title:"Delete Row" />',
          cellClick: (e, cell) => {
            let row = cell.getRow();
            this.tabulator.clearCellValidation(row.getCells());

            if (cell.getTable().getRows().length === 1) 
              cell.getTable().addRow({}, true).then(() => row.delete());
            else row.delete();

            this.updateValidationMessages();
          }
        });
      }
    },
    validate() {
      if (this.disableValidation || this.isReadOnly || !this.tabulator || !this.tabulator.initialized) return;

      if (this.isValidWhenEmpty) {
        let allEmpty = this.tabulator.getRows().every(row => 
          row.getCells().every(cell => cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === "")
        );
        if (allEmpty) {
          this.validationMessage = null;
          this.tabulator.clearCellValidation();
          return;
        }
      }

      this.tabulator.validate();
      this.updateValidationMessages();

      if (this.tabulator.getInvalidCells().length > 0) 
        return this.tabulator.getInvalidCells().map(c => c._cell.modules.validate.invalid);
    },
    updateValidationMessages() {
      if (!this.disableChangeTracker && !this.isReadOnly)
        changeTracker.changeMade(true);

      this.validationMessage = null;
      if (this.disableValidationMessage) return;
      
      let validationErrors = {};
      this.tabulator.getInvalidCells().filter(cell => cell.getRow().getElement() !== null).forEach(cell => {
        let title = cell.getColumn().getDefinition().title;
        let errors = cell._cell.modules.validate.invalid;

        let messages = this.getSampleValidationMessage(errors);

        if (!validationErrors[title]) validationErrors[title] = new Set();
        messages.forEach(m => validationErrors[title].add(m));
      });

      this.validationMessage = Object.entries(validationErrors)
        .map(([field, messages]) => `${field}: ${[...messages].join(", ")}`)
        .join('<br />');
    },
    getSampleValidationMessage(errors) {
      if (!errors || errors.length === 0) return [];

      let errorStrings = [];
      if (errors.some(e => e.type == 'required')) errorStrings.push('cannot be empty');
      else if (errors.some(e => e.type == 'numeric')) errorStrings.push('must be numeric');
      else if (errors.some(e => e.type == 'integer')) errorStrings.push('must be integer');
      else if (errors.some(e => e.type == 'alphanumeric')) errorStrings.push('must be alphanumeric');
      else if (errors.some(e => e.type == 'min')) {
        let minVal = errors.find(e => e.type == 'min').parameters;
        errorStrings.push(`must be at least ${minVal}`);
      }
      else if (errors.some(e => e.type == 'max')) {
        let maxVal = errors.find(e => e.type == 'max').parameters;
        errorStrings.push(`cannot be greater than ${maxVal}`);
      }
      
      if (errors.some(e => e.type == 'unique')) errorStrings.push('must be unique');

      if (errors.some(e => e.type == 'minLength')) {
        let minLength = errors.find(e => e.type == 'minLength').parameters;
        errorStrings.push(`must be at least ${minLength} characters`);
      }
      else if (errors.some(e => e.type == 'maxLength')) {
        let maxLength = errors.find(e => e.type == 'maxLength').parameters;
        errorStrings.push(`cannot be more than ${maxLength} characters`);
      }

      if (errorStrings.length === 0) errorStrings.push('contains error');
      
      return errorStrings;
    }
  },
  watch: {
    value(newValue) {
      this.tableData = newValue;
      this.refreshData();
    },
  }
};
</script>

<style lang="scss">
@import "tabulator-tables/dist/css/tabulator.min.css";

.cursor-pointer {
  cursor: pointer;
}

// Header Selected Color (originally #3876ca)
.tabulator .tabulator-header .tabulator-col.tabulator-range-selected {
  background-color: #006b87;
}
.tabulator-row.tabulator-range-highlight.tabulator-range-selected .tabulator-cell.tabulator-range-row-header,
.tabulator-row.tabulator-range-selected .tabulator-cell.tabulator-range-row-header {
  background-color: #006b87;
}

// Selected Cell Fill Color (originally #9abcea)
.tabulator-row .tabulator-cell.tabulator-range-selected:not(.tabulator-range-only-cell-selected):not(.tabulator-range-row-header) {
  background-color: #c0e3f0
}
.tabulator-row.tabulator-selected {
  background-color: #c0e3f0
}

// Selected Cell Border Color (Originally #2975dd)
.tabulator .tabulator-tableholder .tabulator-range-overlay .tabulator-range {
  border: 1px solid rgb(3, 145, 197);
}
.tabulator .tabulator-tableholder .tabulator-range-overlay .tabulator-range.tabulator-range-active:after {
  background-color: rgb(3, 145, 197);
}
.tabulator .tabulator-tableholder .tabulator-range-overlay .tabulator-range-cell-active {
  border: 2px solid rgb(3, 145, 197);
}

// Originally #769bcc
@media (hover:hover) and (pointer:fine) {
  .tabulator-row.tabulator-selected:hover {
    background-color: #36a9e1;
  }
}

// Originally #1d68cd
.tabulator-row .tabulator-cell.tabulator-editing {
  border: 1px solid #0092c6;
}
.tabulator-edit-list .tabulator-edit-list-item.active {
  background: #0092c6;
}
.tabulator-edit-list .tabulator-edit-list-item.focused {
  outline: 1px solid #0092c6;
}
@media (hover:hover) and (pointer:fine) {
  .tabulator-edit-list .tabulator-edit-list-item:hover {
    background: #0092c6;
  }
}

</style>



// WEBPACK FOOTER //
// src/framework/primitives/AmfSpreadsheet.vue